/**
 * Main Application class
 * Wraps PixiJS Application and manages core systems
 */

import { Application as PixiApp, Container } from 'pixi.js';
import { SceneManager } from './SceneManager';
import { stateMachine } from './StateMachine';
import { eventBus } from './EventBus';
import type { AppConfig } from './types';
import type { SceneContext } from '@scenes/BaseScene';

// Scene imports
import { LandingScene } from '@scenes/LandingScene';
import { RoleSelectScene } from '@scenes/RoleSelectScene';
import { ScenarioScene } from '@scenes/ScenarioScene';
import { SummaryScene } from '@scenes/SummaryScene';

const DEFAULT_CONFIG: AppConfig = {
  width: 1280,
  height: 720,
  backgroundColor: 0x1a1a2e,
  resolution: window.devicePixelRatio || 1,
  antialias: true,
};

export class Application {
  private pixiApp: PixiApp;
  private sceneManager!: SceneManager;
  private sceneContainer: Container;
  private config: AppConfig;
  private isInitialized: boolean = false;

  constructor(config: Partial<AppConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.pixiApp = new PixiApp();
    this.sceneContainer = new Container();
  }

  /**
   * Initialize the application
   */
  async init(container: HTMLElement): Promise<void> {
    if (this.isInitialized) {
      console.warn('Application already initialized');
      return;
    }

    // Initialize PixiJS
    await this.pixiApp.init({
      width: this.config.width,
      height: this.config.height,
      backgroundColor: this.config.backgroundColor,
      resolution: this.config.resolution,
      antialias: this.config.antialias,
      autoDensity: true,
      resizeTo: container,
    });

    // Remove loading indicator and add canvas
    const loadingEl = document.getElementById('loading');
    if (loadingEl) {
      loadingEl.remove();
    }
    container.appendChild(this.pixiApp.canvas);

    // Set up scene container
    this.pixiApp.stage.addChild(this.sceneContainer);

    // Create scene context
    const sceneContext: SceneContext = {
      width: this.pixiApp.screen.width,
      height: this.pixiApp.screen.height,
      resolution: this.config.resolution,
    };

    // Initialize scene manager
    this.sceneManager = new SceneManager(this.sceneContainer, sceneContext);

    // Register scenes
    this.registerScenes();

    // Set up resize handling
    this.setupResizeHandler(container);

    // Set up game loop
    this.pixiApp.ticker.add(this.update.bind(this));

    // Set up visibility handling
    this.setupVisibilityHandler();

    this.isInitialized = true;
  }

  /**
   * Register all scenes
   */
  private registerScenes(): void {
    this.sceneManager.register('landing', LandingScene);
    this.sceneManager.register('role-select', RoleSelectScene);
    this.sceneManager.register('scenario', ScenarioScene);
    this.sceneManager.register('summary', SummaryScene);
  }

  /**
   * Set up window resize handling
   */
  private setupResizeHandler(container: HTMLElement): void {
    const resize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      this.pixiApp.renderer.resize(width, height);
      this.sceneManager.onResize(width, height);
    };

    window.addEventListener('resize', resize);
    resize();
  }

  /**
   * Set up visibility change handling (pause when tab hidden)
   */
  private setupVisibilityHandler(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.sceneManager.pause();
      } else {
        this.sceneManager.resume();
      }
    });
  }

  /**
   * Main update loop
   */
  private update(): void {
    const delta = this.pixiApp.ticker.deltaMS / 1000;
    this.sceneManager.update(delta);
  }

  /**
   * Start the application
   */
  async start(): Promise<void> {
    if (!this.isInitialized) {
      throw new Error('Application not initialized. Call init() first.');
    }

    // Always start fresh for now - prevents corrupted session issues
    // TODO: Add proper session resume logic with validation
    stateMachine.resetSession();
    await this.sceneManager.changeTo('landing');
  }

  /**
   * Navigate to a specific scene
   */
  async navigateTo(sceneName: string): Promise<void> {
    await this.sceneManager.changeTo(sceneName);
  }

  /**
   * Get the scene manager
   */
  getSceneManager(): SceneManager {
    return this.sceneManager;
  }

  /**
   * Get the PixiJS application
   */
  getPixiApp(): PixiApp {
    return this.pixiApp;
  }

  /**
   * Clean up and destroy the application
   */
  destroy(): void {
    eventBus.clear();
    this.sceneManager.destroy();
    this.pixiApp.destroy(true, { children: true });
    this.isInitialized = false;
  }
}

// Export singleton instance
export const app = new Application();
